import React, { ReactFragment } from 'react';
import { RoutePath } from 'enums/Routes';
import { EmployerPortalData, GetApplicationData } from 'handlers/applicationData';
import { VerificationStep } from 'components/Verification/verificationSteps';
import { YourIncomeVariant } from 'handlers/yourIncome';
import { PhoneNumbers, PhoneNumbersLabels } from 'enums/PhoneNumbers';
import { ErrorType } from 'components/Error/ErrorType';
import { formatPhoneNumber } from 'utils/formatPhoneNumber';

import styles from './CommonQuestions.module.scss';

export interface Question {
  question: string;
  answer: ReactFragment;
}

const privacyPolicyLink = (
  <a href="https://www.planneryapp.com/privacy-policy" target="_blank" rel="noreferrer">
    Privacy Policy
  </a>
);

const infoSecure = {
  question: 'How do you keep my information secure?',
  answer: (
    <p>
      Our information is transferred and stored using the latest encryption standards. We do not sell your information,
      or share any information with third parties other than those required to provide you with our service (for
      example, looking up your credit report). To learn more, please read our {privacyPolicyLink}.
    </p>
  ),
};

const whatCreditScore = {
  question: 'What credit score will I need to be approved?',
  answer: (
    <p>
      You need a minimum of 580 credit score to be approved. In addition to your credit score, we consider other factors
      like your total income, outstanding debt, delinquency status and your employment tenure to approve your
      application.
    </p>
  ),
};

const isCreditAffected = {
  question: 'Will applying affect my credit?',
  answer: <p>We do a soft pull to confirm your liabilities. Your score will not be affected.</p>,
};

const whyStudentLoanEstimate = {
  question: 'Why is this just an estimate?',
  answer:
    'Your credit report shows your balance but not your interest rates, repayment plan or PSLF progress. Your studentaid.gov file fills those in, and your servicer sets the final payment when you apply.',
};

const isForgivenessTaxed = {
  question: 'Is forgiveness taxed?',
  answer:
    'PSLF forgiveness is tax-free. Forgiveness after 20 to 30 years on an income-driven plan counts as income; we estimate 22% federal tax.',
};

const studentLoanEmployerQuestions: Question[] = [
  {
    question: 'Why does my employer matter?',
    answer:
      'PSLF only counts work for government or 501(c)(3) nonprofit employers. If a staffing agency or physician group pays you, that company is your employer, even when you work in a hospital.',
  },
  {
    question: 'Is this a hard credit check?',
    answer: "No. We already have your credit report from the soft pull, which doesn't affect your score.",
  },
];

const studentLoanWorkQuestions: Question[] = [
  {
    question: 'What counts as full-time?',
    answer: 'At least 30 hours a week on average, at one or more qualifying employers combined.',
  },
  {
    question: 'I work per diem. Can I still qualify?',
    answer: 'Yes, if your hours across qualifying employers average 30 or more a week.',
  },
];

const studentLoanHouseholdQuestions: Question[] = [
  {
    question: 'Who counts as a dependent?',
    answer:
      "Children and others you claim on your federal tax return. Don't count your spouse here; we ask about your spouse separately.",
  },
  {
    question: 'Why do you ask how I file?',
    answer: 'Filing jointly adds your spouse’s income to the payment formula. Filing separately usually leaves it out.',
  },
];

const studentLoanIncomeQuestions: Question[] = [
  {
    question: 'What is AGI?',
    answer:
      'Adjusted gross income: your income after pre-tax deductions like 403(b) contributions and health premiums. Income-driven payments are based on it.',
  },
  {
    question: 'Is there a shortcut?',
    answer: 'Box 1 of your W-2 is close, because it already leaves out most pre-tax deductions.',
  },
];

const studentLoanEstimateQuestions: Question[] = [
  whyStudentLoanEstimate,
  {
    question: 'Why is it a range?',
    answer:
      'Past full-time work at nonprofit or government employers may already count toward PSLF, but only if you were on a qualifying plan. Your file shows your real count.',
  },
  isForgivenessTaxed,
];

const studentLoanUploadQuestions: Question[] = [
  {
    question: 'Is my file safe?',
    answer: 'We only use it to build your plan. We never ask for your studentaid.gov password.',
  },
  {
    question: 'Why can’t you pull it for me?',
    answer: 'The Department of Education doesn’t let apps log in for you, so the download has to come from you.',
  },
];

const studentLoanPlanQuestions: Question[] = [whyStudentLoanEstimate, isForgivenessTaxed];

const repaymentMethodQuestions: Question[] = [
  {
    question: 'Why do I need to direct deposit from my paycheck?',
    answer:
      'By having payments come straight from your paycheck, we reduce the chance of missed payments. This lowers the cost for Plannery, and we pass on the savings to you.',
  },
  {
    question: 'Is this similar to wage garnishment?',
    answer:
      'No. Wage garnishment is when a court takes money from your paycheck without your consent. With Plannery, this is a voluntary step to access low interest rates. You remain in control at all times.',
  },
];

const whatIsPlaid = {
  question: 'What is Plaid and why did we partner with them?',
  answer: (
    <>
      Here are some resources you might find helpful:
      <div>
        <p>
          <a href="https://plaid.com/how-it-works-for-consumers/" target="_blank" rel="noreferrer">
            How Plaid Works
          </a>
        </p>
        <p>
          <a href="https://plaid.com/how-we-handle-data//" target="_blank" rel="noreferrer">
            How Plaid Handles Data
          </a>
        </p>
        <p>
          <a href="https://plaid.com/why-is-plaid-involved/" target="_blank" rel="noreferrer">
            Why is Plaid Involved
          </a>
        </p>
      </div>
    </>
  ),
};

interface GetQuestionsProps {
  application: GetApplicationData | undefined;
  verificationStep?: VerificationStep;
  variant: YourIncomeVariant;
  isStudentLoanForgivenessFlow: boolean;
  employerPortalDataInstructions?: EmployerPortalData;
  navigate: (path: RoutePath, state?: any) => void;
  directDepositInstructions?: boolean;
  plaidAccountError?: boolean;
}

const getQuestions = (
  path: RoutePath,
  {
    application,
    verificationStep,
    variant,
    isStudentLoanForgivenessFlow,
    employerPortalDataInstructions,
    navigate,
    directDepositInstructions,
    plaidAccountError,
  }: GetQuestionsProps,
): Question[] => {
  const hasOriginationFee = (application?.originationFee ?? 0) > 0;
  const hasHrContactInfo =
    employerPortalDataInstructions?.hrTeamName &&
    employerPortalDataInstructions?.phoneNumber &&
    employerPortalDataInstructions?.email;
  const phoneNumberFormatted = formatPhoneNumber(employerPortalDataInstructions?.phoneNumber);

  const HRContactContainer = (label: string) => (
    <div className={styles.contactContainer}>
      <p>{label}</p>

      {hasHrContactInfo && (
        <>
          <div>
            <p className={styles.title}>{employerPortalDataInstructions?.hrTeamName}</p>
            <p className={styles.subtitle}>{employerPortalDataInstructions?.attentionRange}</p>
          </div>
          <p>
            Call{' '}
            <span className={styles.highlightText} onClick={() => window.open(`sms://${phoneNumberFormatted}`)}>
              {employerPortalDataInstructions?.phoneNumber}
            </span>{' '}
            or send an{' '}
            <span
              className={styles.highlightText}
              onClick={() => window.open(`mailto:${employerPortalDataInstructions?.email}`)}
            >
              email
            </span>
            .
          </p>
        </>
      )}
    </div>
  );

  switch (path) {
    case RoutePath.Offer:
      return [
        {
          question: 'How long does it take to apply?',
          answer: (
            <p>Our application takes less than 5 minutes for you to complete and receive a pre-approved offer.</p>
          ),
        },
        {
          question: 'When do I get my loan money?',
          answer: (
            <p>
              After you sign the loan documents and set up your payment process, you could receive the funding within
              one to three business days.
            </p>
          ),
        },
        {
          question: 'How are you able to offer lower rates compared to other lenders?',
          answer: (
            <>
              <p>
                Unlike other lenders, we are solely focused on serving one community - healthcare professionals.
                Understanding the profile of healthcare professionals and addressing their needs distinguishes us from
                the competition.
              </p>
              <p>
                By taking repayments directly from your paycheck, we are able to offer lower rates while helping you to
                maintain on-time payments.
              </p>
            </>
          ),
        },
        whatCreditScore,
        isCreditAffected,
      ];
    case RoutePath.YourName:
      return [infoSecure];
    case RoutePath.AICoachMonthlySavings:
      return [
        {
          question: 'Why do you need this information?',
          answer: (
            <p>
              Understanding what you are comfortable putting towards your primary goal today helps us understand what
              your starting point is. We will help you find ways to accelerate your path towards your goals once we have
              a full view of your finances.
            </p>
          ),
        },
      ];
    case RoutePath.YourIncome:
      if (isStudentLoanForgivenessFlow) {
        return [isCreditAffected, infoSecure];
      }
      switch (variant) {
        case YourIncomeVariant.Default:
        case YourIncomeVariant.CardCrossSell:
          return [
            {
              question: 'Will you consider my public assistance income?',
              answer:
                'Yes. income from all sources including public assistance, alimony child support, disability will be considered to qualify you for a loan. We will require proof of income if you decide to move forward.',
            },
            isCreditAffected,
            whatCreditScore,
            infoSecure,
          ];
        case YourIncomeVariant.CardWaitlist:
          return [infoSecure];
        default:
      }
      return [infoSecure];
    case RoutePath.FinalizeReviewOffer:
    case RoutePath.ConfirmLoan:
      return [
        {
          question: 'What’s the difference between Interest Rate and APR?',
          answer: (
            <>
              <p>
                The <strong>Interest Rate</strong> is used to calculate your paycheck payment amount.
              </p>
              <p>
                The <strong>APR</strong> (Annual Percentage Rate) includes both the Interest Rate and the one-time
                origination fee, shown as a yearly cost.
              </p>
            </>
          ),
        },
        {
          question: 'Why do I see 2 percentages for each loan option?',
          answer: (
            <>
              <p>We show you both:</p>
              <ul className={styles.list}>
                <li>
                  <strong>Interest Rate</strong> = what sets your paycheck payment.
                </li>
                <li>
                  <strong>APR</strong> = Interest Rate plus the one-time origination fee.
                </li>
              </ul>
              <p>They are two ways of showing cost. You do not add them together.</p>
            </>
          ),
        },
        {
          question: 'When do I sign the final loan agreement?',
          answer: (
            <p>
              You will sign the final loan agreement after we verify your income, identity, and repayment is set up.
            </p>
          ),
        },
        {
          question: 'What fees do you charge?',
          answer: hasOriginationFee ? (
            <p>
              We charge an origination fee, which is displayed in the loan breakdown. There is also a fee for missed
              payments, but we will set you up with automatic payments to avoid this. All of the fees are disclosed in
              the loan agreement.
            </p>
          ) : (
            <p>
              We charge a fee for missed payments, but we will set you up with automatic payments to avoid this. Our
              fees are disclosed in the loan agreement.
            </p>
          ),
        },
        {
          question: 'Why is there an origination fee for the loan?',
          answer: (
            <p>
              The origination fee covers our cost of offering you the loan. We add this fee on to the total loan amount,
              so you don’t have to pay anything upfront.
            </p>
          ),
        },
        {
          question: 'How long is this offer valid for?',
          answer: (
            <p>
              This offer is valid for 30 days from the date that the offer was generated. After that time, you’ll have
              to reapply to get a new offer.
            </p>
          ),
        },
      ];
    case RoutePath.VerificationSteps:
      switch (verificationStep) {
        case VerificationStep.UploadPaystubs:
          if (application?.notEmployed) {
            return [];
          }
          return [
            {
              question: 'Why do I have to upload paystubs?',
              answer: (
                <p>
                  This helps us verify your employer, income, and pay period so you can receive the lowest interest
                  rates available.
                </p>
              ),
            },
            {
              question: 'How can I get my paystubs?',
              answer: (
                <>
                  <p>Log into your payroll system to download your paystubs.</p>
                  <p>
                    If you need help, click the ‘Need Help’ link at the bottom of the page to begin a text conversation
                    or feel call/text us at {PhoneNumbersLabels.Support}.
                  </p>
                </>
              ),
            },
          ];

        case VerificationStep.CheckingAccount:
          if (plaidAccountError) {
            return [];
          }
          return [
            {
              question: 'Why do I have to do this?',
              answer: (
                <>
                  <p>
                    We ask you to connect your checking account for a few reasons: depending on your offer, we may have
                    to send funds directly to you so you can pay off the lenders on your behalf. Secondly, we use it as
                    a means to verify your income. Lastly, with your consent, this will be used to set up for automatic
                    payments to ensure your loan stays on track.
                  </p>
                </>
              ),
            },
            {
              question: 'What information are you retrieving?',
              answer: `We retrieve your account and routing number to ensure they are correct when we deposit funds. We also retrieve your recent transactions so we can check that you've been paid what your paystubs indicate.`,
            },
            whatIsPlaid,
          ];
        default:
          return [];
      }
    case RoutePath.Alloy:
      return [
        {
          question: 'What is Alloy?',
          answer: (
            <>
              <p>
                Alloy helps banks and financial technology companies automate and manage their decisions for onboarding,
                ongoing fraud & suspicious activity monitoring, and credit underwriting.
              </p>
              <p>
                Alloy processes millions of daily identity decisions for the world’s leading financial institutions,
                offering financial products in 40 countries.
              </p>
              <p>
                Learn more about Alloy by visiting their{' '}
                <a href="https://www.alloy.com/about" target="_blank" rel="noreferrer">
                  website
                </a>
                .
              </p>
            </>
          ),
        },
      ];
    case RoutePath.OfferAvailable:
      return application?.canSwitchPaymentMethods ? repaymentMethodQuestions : [];
    case RoutePath.RepaymentMethod:
      return repaymentMethodQuestions;
    case RoutePath.OpenDDA:
      return [
        {
          question: 'Do I have to open this account to get the loan?',
          answer: `No. Having the direct deposit account makes it much easier to make payments for the loan. However, if you do not want to set this up, please call or text us at ${PhoneNumbers.Support}.`,
        },
        {
          question: 'Why can’t I use my existing bank account?',
          answer: `This bank account is created solely for the purpose of depositing and transferring payments for your Plannery loan. By having this as a separate bank account, we are able to set up the automation necessary to pay back the loan from your paycheck. If you do not want to set up this account, please call or text us at ${PhoneNumbers.Support}.`,
        },
        {
          question: 'Can I use this as a checking account?',
          answer:
            'No. This account is solely for the purpose of depositing and transferring payments for your Plannery loan. We don’t offer any other banking features at this time.',
        },
        {
          question: 'Are there any fees associated with the account?',
          answer:
            'No. It is a no-cost account. There is no minimum balance requirement. Since this account is solely for the purpose of depositing and transferring payments for your Plannery loan, the majority of the time your account will have zero balance.',
        },
        {
          question: 'How will I be able to close the account in the future?',
          answer:
            'Your account will be closed automatically when your loan is fully repaid. You may also contact us if you would like to close your account and repay your Plannery loan by alternate means.',
        },
        {
          question: 'What happens if my employer changes?',
          answer:
            'If your employer changes, you will reconnect the loan to your new employer’s payroll system. We can also update your loan to match your new employer’s pay cycle.',
        },
        {
          question: 'What happens if I lose my job?',
          answer:
            'If payroll doesn’t run during a pay period, we will automatically deduct the money from your checking account that you provided earlier. We know life happens. If you are unable to pay, give us a call as soon as you can.',
        },
        ...(application?.fullDepositSwitch
          ? [
              {
                question: 'Will this change when I get access to my paycheck?',
                answer:
                  'Once your paycheck is deposited to your Plannery deposit account and your loan payment is debited, the remaining balance will be sent to your external checking account.  As long as cutoff times are met, you will have access to your money that same day.',
              },
            ]
          : []),
      ];
    case RoutePath.SetupDeduction:
      return [
        {
          question: 'How do I know if I have a payroll system?',
          answer: HRContactContainer('Ask your HR representative to help you access the system.'),
        },
      ];
    case RoutePath.StudentLoanEmployer:
      return studentLoanEmployerQuestions;
    case RoutePath.StudentLoanWork:
      return studentLoanWorkQuestions;
    case RoutePath.StudentLoanHousehold:
      return studentLoanHouseholdQuestions;
    case RoutePath.StudentLoanIncome:
      return studentLoanIncomeQuestions;
    case RoutePath.StudentLoanEstimate:
      return studentLoanEstimateQuestions;
    case RoutePath.StudentLoanUploadAidData:
      return studentLoanUploadQuestions;
    case RoutePath.StudentLoanPlan:
      return studentLoanPlanQuestions;
    case RoutePath.UnfreezeCreditScore:
      return [
        {
          question: 'What is a credit freeze?',
          answer:
            'A credit freeze is a security measure that prevents lenders from accessing your Equifax credit report without your permission.',
        },
        {
          question: 'How do I continue my application?',
          answer:
            'We just sent you an email with a link you can use once you’re ready. Or simply log into your Plannery account to return where you left off.',
        },
      ];
    case RoutePath.BureauVerification:
      return [
        {
          question: 'Why do I need to verify my identity?',
          answer:
            'The credit bureau could not automatically verify your identity from the information provided. Completing the text message link helps them confirm it is you.',
        },
        {
          question: 'How do I continue my application?',
          answer:
            'Open the text message from the bureau, complete the verification link, then return here and tap Continue application. Or log into your Plannery account to pick up where you left off.',
        },
      ];
    case RoutePath.ExtendedIdentityCheck:
    case RoutePath.ExtendedIdentityAddresses:
      return [
        {
          question: 'Why do you need more details?',
          answer:
            'We could not match a credit file with the information we have. Extra details like a middle name, full Social Security number, or another address help us find the right profile.',
        },
        {
          question: 'Do I have to fill everything in?',
          answer:
            'No. Every field is optional. Share whatever you can, then continue so we can try matching your credit profile again.',
        },
      ];
    case RoutePath.SetupViaPayroll:
    case RoutePath.PaystubsSignInGuide:
      return [
        {
          question: 'How does this work?',
          answer:
            'In this step, you are setting up your repayment to come from your paycheck. These payments are sent to your new direct deposit account and used to repay your Plannery loan. This automates the payment process and allows us to offer you some of the best rates in the industry.',
        },
        {
          question: 'I don’t know how to set up direct deposit.',
          answer: (
            <p>
              If the directions don’t make sense to you,{' '}
              <span
                className={styles.highlightText}
                onClick={() => navigate(RoutePath.Error, { state: { type: ErrorType.CantSetUpPayrollDeduction } })}
              >
                contact Plannery
              </span>
              .
            </p>
          ),
        },
        ...(RoutePath.SetupViaPayroll === path && !directDepositInstructions
          ? [
              {
                question: 'The routing number is not recognized.',
                answer: (
                  <p>
                    <span className={styles.highlightText} onClick={() => navigate(RoutePath.NotifyYourTeam)}>
                      Send an email
                    </span>{' '}
                    to your benefits team.
                  </p>
                ),
              },
            ]
          : []),
        ...(hasHrContactInfo && !directDepositInstructions
          ? [
              {
                question: 'I have other questions.',
                answer: HRContactContainer('For other questions, contact:'),
              },
            ]
          : []),
      ];
    case RoutePath.PlaidCheckingAccounts:
      return [
        {
          question: 'Why do I have to do this?',
          answer:
            'Connecting your accounts allows us to get a baseline understanding of your income and expense patterns. This is critical for Plannery to be able to offer your relevant and personalized assistance.',
        },
        {
          question: 'What information are you retrieving?',
          answer:
            'We retrieve transactions on your account and classify them as income or expenses and classify expenses based on categories to help you get a clear understanding of your finances.',
        },
        whatIsPlaid,
      ];
    default:
      return [];
  }
};

export default getQuestions;
